import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashWelcome from '../components/DashWelcome';
import { Button } from '../components/Button';
import TabNavigation from '../components/TabNavigation';
import useSectionLayout from '../hooks/useSectionLayout';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import VisionSection from '../components/VisionSection';

// Section imports
import JournalSection from '../components/JournalSection';
import { PlannerSection } from '../components/PlannerSection';
import ActionSection from '../components/ActionSection';
import CelebrateSection from '../components/CelebrateSection';
import GrowthInstructions from '../components/GrowthInstructions';
import GoldFileSection from '../components/GoldFileSection';
import GrowthOpportunitiesSection from '../components/GrowthOpportunitiesSection';
import GoalsSection from '../components/GoalsSection';
import ProgressSection from '../components/ProgressSection';
import CommunitySection from '../components/CommunitySection';

export function DashGrowthLoop() {
  const navigate = useNavigate();
  const { locations, isLoading } = useSectionLayout();
  const [user, setUser] = useState(null);

  const [openSections, setOpenSections] = useState({
    planner: false,
    gold_file: false,
    progress: false,
    growth_opportunities: false,
    goals: false,
    community: false,
    vision: false
  });

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const [showBanner, setShowBanner] = useState(false);
  const [growthStatus, setGrowthStatus] = useState({
    journaled: false,
    task_completed: false,
    lesson_learned: false
  });

  useEffect(() => {
    const initializeUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
      }
    };

    const checkCoreValues = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('core_value_1, core_value_2, core_value_3')
        .eq('id', session.user.id)
        .single();

      if (
        !profile?.core_value_1 ||
        !profile?.core_value_2 ||
        !profile?.core_value_3
      ) {
        setShowBanner(true);
      }
    };

    const fetchGrowthStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from('growth_days')
        .select('journaled, task_completed, lesson_learned')
        .eq('user_id', session.user.id)
        .eq('date', today)
        .maybeSingle();

      if (data) {
        setGrowthStatus({
          journaled: !!data.journaled,
          task_completed: !!data.task_completed,
          lesson_learned: !!data.lesson_learned
        });
      }
    };

    initializeUser();
    checkCoreValues();
    fetchGrowthStatus();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-gray-600 mt-4">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const showGrowthInstructions = locations?.growth_instructions === 'loop';
  const showGoldFile = locations?.gold_file === 'loop';
  const showGrowthOpportunities = locations?.growth_opportunities === 'loop';
  const showGoals = locations?.goals === 'loop';
  const showProgress = true;
  const showCommunity = locations?.community === 'loop';
  const showVision = locations?.vision === 'loop';

  return (
    <ProtectedLayout>
      <div className="p-4 max-w-4xl mx-auto space-y-6 relative">
        <DashWelcome />

        {showBanner && (
          <div className="bg-yellow-100 text-yellow-900 p-4 rounded-lg shadow border border-yellow-300 mb-4">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                Please complete your onboarding to unlock the full experience.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/impact-intentions')}
              >
                Finish Onboarding
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-6 mt-6">
          {showGrowthInstructions && <GrowthInstructions />}
          <JournalSection />
          {user && (
            <PlannerSection
              userId={user.id}
              isOpen={openSections.planner}
              onToggle={toggleSection}
              sectionKey="planner"
            />
          )}
          <ActionSection />
          <CelebrateSection />
          
          {showVision && (
            <VisionSection
              open={openSections.vision}
              toggle={toggleSection}
              sectionKey="vision"
            />
          )}
          
          {showGoldFile && (
            <GoldFileSection
              open={openSections.gold_file}
              toggle={toggleSection}
            />
          )}
          {showGrowthOpportunities && (
            <GrowthOpportunitiesSection
              open={openSections.growth_opportunities}
              toggle={toggleSection}
              sectionKey="growth_opportunities"
              progress={{}}
              toggleItem={() => {}}
            />
          )}
          {showGoals && (
            <GoalsSection
              open={openSections.goals}
              toggle={toggleSection}
              sectionKey="goals"
            />
          )}
          {showProgress && (
            <ProgressSection
              open={openSections.progress}
              toggle={toggleSection}
              sectionKey="progress"
            />
          )}
          {showCommunity && (
            <CommunitySection
              open={openSections.community}
              toggle={toggleSection}
              sectionKey="community"
            />
          )}
        </div>

        <TabNavigation />
      </div>
    </ProtectedLayout>
  );
}